import * as React from 'react';
import api from '../api/api';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';


import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

import {
  DataGrid,
  GridColDef,
  GridRowModel,
  GridActionsCellItem,
  GridToolbarContainer,
  GridRowModes,
  GridRowModesModel,
  GridRenderCellParams,
  GridFilterModel,
  GridRenderEditCellParams,
  GridRow,
  GridRowProps,
} from '@mui/x-data-grid';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';


const RowDraggableContext = React.createContext<{
  attributes: Record<string, any>;
  listeners: SyntheticListenerMap | undefined;
}>({
  attributes: {},
  listeners: undefined,
});

const RowDragHandle = () => {
  const { attributes, listeners } = React.useContext(RowDraggableContext);
  return (
    <DragIndicatorIcon
      {...attributes}
      {...listeners}
      sx={{ color: 'text.disabled', cursor: 'grab' }}
    />
  );
};

const DraggableRow = (props: GridRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.rowId,
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 1,
    position: 'relative' as const,
    opacity: isDragging ? 0.8 : 1,
    backgroundColor: isDragging ? '#f0f9ff' : props.style?.backgroundColor,
  };

  return (
    <RowDraggableContext.Provider value={{ attributes, listeners }}>
      <GridRow
        {...props}
        ref={setNodeRef}
        style={style}
      />
    </RowDraggableContext.Provider>
  );
};



type CommandType = '2g' | '4g';

type CommandRow = {
  id: string;
  _id?: string;
  type: CommandType;
  command: string;
  description?: string;
  expectedResponse?: string;
  notes?: string;
  requiresInputValue?: boolean;
  inputValue?: string;
  isNew?: boolean;
  order?: number;
};

type ToastMessage = {
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
};



const API_ENDPOINT = '/api/commands';
const SEARCH_DEBOUNCE_MS = 300;
const TOAST_DURATION_MS = 3000;


function generateTempId(): string {
  return `tmp_${Math.random().toString(36).slice(2, 11)}`;
}

function validateCommandRow(row: CommandRow): string | null {
  if (!row.command?.trim()) {
    return 'Command is required';
  }
  if (!row.description?.trim()) {
    return 'Description is required';
  }
  return null;
}



interface CommandToolbarProps {
  title: string;
  rows: readonly CommandRow[];
  setRows: React.Dispatch<React.SetStateAction<readonly CommandRow[]>>;
  setRowModesModel: React.Dispatch<React.SetStateAction<GridRowModesModel>>;
  type: CommandType;
  searchText: string;
  setSearchText: (value: string) => void;
  onReload: () => void;
}


function CommandToolbar({
  title,
  rows,
  setRows,
  setRowModesModel,
  type,
  searchText,
  setSearchText,
}: CommandToolbarProps) {
  const handleAdd = React.useCallback(() => {
    const id = generateTempId();
    const oldNewRow = rows.find(r => r.isNew);

    setRowModesModel((prev) => {
      const updated = { ...prev };

      // If we are about to delete an old 'isNew' row, remove it from the model entirely
      if (oldNewRow) {
        delete updated[oldNewRow.id];
      }

      // Set other rows to View
      Object.keys(updated).forEach((rowId) => {
        if (updated[rowId]?.mode === GridRowModes.Edit) {
          updated[rowId] = { mode: GridRowModes.View, ignoreModifications: true };
        }
      });

      updated[id] = { mode: GridRowModes.Edit, fieldToFocus: 'command' };
      return updated;
    });


    setRows((prev) => [
      {
        id,
        type,
        command: '',
        description: '',
        expectedResponse: '',
        notes: '',
        requiresInputValue: false,
        inputValue: '',
        isNew: true,
      },
      ...prev.filter((r) => !r.isNew),
    ]);
  }, [type, rows, setRows, setRowModesModel]);

  return (
    <GridToolbarContainer
      sx={{
        px: 2,
        py: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>

        <TextField
          size="small"
          placeholder="Search by command or description..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ width: { xs: '100%', sm: 320 } }}
        />
      </Box>

      <Tooltip title="Add command">
        <IconButton
          onClick={handleAdd}
          size="medium"
          color="primary"
          sx={{
            mt: 0.5,
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          <AddIcon />
        </IconButton>
      </Tooltip>
    </GridToolbarContainer>
  );
}



interface DataGridEditorProps {
  type: CommandType;
}

function DataGridEditor({ type }: DataGridEditorProps) {
  const [rows, setRows] = React.useState<readonly CommandRow[]>([]);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [toast, setToast] = React.useState<ToastMessage | null>(null);
  const [quickFilterText, setQuickFilterText] = React.useState('');
  const [searchInput, setSearchInput] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );



  const loadCommands = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(API_ENDPOINT, { params: { type } });
      const commands: CommandRow[] = (response.data || [])
        .filter((row: any) => row.type?.toLowerCase() === type)
        .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)) // sort by order field
        .map((row: any) => ({ ...row, id: row._id }));
      setRows(commands);
    } catch (err) {
      const errorMessage = 'Failed to load commands. Please try again.';
      setError(errorMessage);
      setToast({ message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [type]);

  React.useEffect(() => {
    loadCommands();
  }, [loadCommands]);



  React.useEffect(() => {
    const timer = setTimeout(() => {
      setQuickFilterText(searchInput);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput]);



  const filterModel: GridFilterModel = React.useMemo(
    () => ({
      items: [],
      quickFilterValues: quickFilterText ? [quickFilterText] : undefined,
    }),
    [quickFilterText]
  );



  const handleEditClick = React.useCallback((id: string) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.Edit },
    }));
  }, []);

  const handleSaveClick = React.useCallback((id: string) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View },
    }));
  }, []);

  const handleCancelClick = React.useCallback((id: string) => {
    setRowModesModel((prev) => ({
      ...prev,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    }));


    setRows((prev) => prev.filter((row) => row.id !== id || !row.isNew));
  }, []);

  const handleDeleteClick = React.useCallback(
    async (id: string) => {
      if (!window.confirm('Are you sure you want to delete this command?')) {
        return;
      }

      const row = rows.find((r) => r.id === id);
      if (!row) return;


      if (row.isNew) {
        setRows((prev) => prev.filter((r) => r.id !== id));
        setToast({ message: 'Command removed', severity: 'info' });
        return;
      }

      try {
        await api.delete(`${API_ENDPOINT}/${row._id || row.id}`);
        setRows((prev) => prev.filter((r) => r.id !== id));
        setToast({ message: 'Command deleted successfully', severity: 'success' });
      } catch (err) {
        setToast({ message: 'Failed to delete command', severity: 'error' });
      }
    },
    [rows]
  );


  const processRowUpdate = React.useCallback(
    async (newRow: GridRowModel): Promise<CommandRow> => {
      const row = newRow as CommandRow;


      const validationError = validateCommandRow(row);
      if (validationError) {
        setToast({ message: validationError, severity: 'error' });
        throw new Error(validationError);
      }

      try {
        let updatedRow: CommandRow;


        if (row.isNew) {
          const response = await api.post(API_ENDPOINT, {
            type,
            command: row.command.trim(),
            description: row.description?.trim() || '',
            expectedResponse: row.expectedResponse?.trim() || '',
            notes: row.notes?.trim() || '',
            requiresInputValue: row.requiresInputValue || false,
            inputValue: row.inputValue?.trim() || '',
          });

          const mongoId = response.data?.id;
          if (!mongoId) {
            throw new Error('No ID returned from server');
          }

          updatedRow = {
            ...row,
            id: row.id,
            _id: mongoId,
            command: row.command.trim(),
            description: row.description?.trim() || '',
            expectedResponse: row.expectedResponse?.trim() || '',
            notes: row.notes?.trim() || '',
            requiresInputValue: row.requiresInputValue || false,
            inputValue: row.inputValue?.trim() || '',
            isNew: false,
          };

          setToast({ message: 'Command created successfully', severity: 'success' });
        }

        else {
          await api.put(`${API_ENDPOINT}/${row._id}`, {
            command: row.command.trim(),
            description: row.description?.trim() || '',
            expectedResponse: row.expectedResponse?.trim() || '',
            notes: row.notes?.trim() || '',
            requiresInputValue: row.requiresInputValue || false,
            inputValue: row.inputValue?.trim() || '',
          });

          updatedRow = {
            ...row,
            id: row.id,
            command: row.command.trim(),
            description: row.description?.trim() || '',
            expectedResponse: row.expectedResponse?.trim() || '',
            notes: row.notes?.trim() || '',
            requiresInputValue: row.requiresInputValue || false,
            inputValue: row.inputValue?.trim() || '',
          };


          setToast({ message: 'Command updated successfully', severity: 'success' });
        }
        setRows((prev) =>
          prev.map((r) => (r.id === updatedRow.id ? updatedRow : r))
        );


        return updatedRow;
      } catch (err) {
        const errorMessage = row.isNew
          ? 'Failed to create command'
          : 'Failed to update command';
        setToast({ message: errorMessage, severity: 'error' });
        throw err;
      }
    },
    [type]
  );

  /* Checkbox Edit Cell*/

  const renderCheckboxEditCell = React.useCallback(
    (params: GridRenderEditCellParams) => {
      const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.checked;

        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: newValue,
        });

        // Clear inputValue if checkbox is unchecked
        if (!newValue) {
          params.api.setEditCellValue({
            id: params.id,
            field: 'inputValue',
            value: '',
          });
        }

        // Update local state
        setRows((prev) =>
          prev.map((r) =>
            r.id === params.id
              ? {
                ...r,
                requiresInputValue: newValue,
                inputValue: newValue ? r.inputValue : '',
              }
              : r
          )
        );
      };

      return <Checkbox checked={params.value || false} onChange={handleChange} />;
    },
    []
  );

  /* ---------- Input Value Cell Renderers ---------- */

  const renderInputValueCell = React.useCallback(
    (params: GridRenderCellParams) => {
      const row = rows.find((r) => r.id === params.id);
      const hasValue = !!params.value;

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            px: 1,
            color: hasValue ? 'text.primary' : 'text.disabled',
          }}
        >
          {params.value || '—'}
        </Box>
      );
    },
    [rows]
  );

  const renderInputValueEditCell = React.useCallback(
    (params: GridRenderEditCellParams) => {
      const row = rows.find((r) => r.id === params.id);
      const isDisabled = !row?.requiresInputValue;

      const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        params.api.setEditCellValue({
          id: params.id,
          field: params.field,
          value: event.target.value,
        });
      };

      return (
        <TextField
          size="small"
          fullWidth
          value={params.value || ''}
          onChange={handleChange}
          disabled={isDisabled}
          placeholder={isDisabled ? 'Input not required' : 'Enter input value'}
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
              bgcolor: 'action.disabledBackground',
            },
          }}
        />
      );
    },
    [rows]
  );

  /* ---------- Custom Edit Cell Renderers ---------- */
  const RequiredEditInput = (params: GridRenderEditCellParams) => {
    const { id, value, field, api } = params;
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      api.setEditCellValue({ id, field, value: e.target.value });
    };
    return (
      <TextField
        value={value || ''}
        onChange={handleChange}
        fullWidth
        placeholder="Required"
        size="small"
      />
    );
  };

  /* ---------- Column Definitions ---------- */

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'dragHandle',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        renderCell: () => <RowDragHandle />,
      },
      {
        field: 'sno',
        headerName: 'S.No.',
        width: 70,
        editable: false,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) =>
          params.api.getAllRowIds().indexOf(params.id) + 1,
      },
      {
        field: 'command',
        headerName: 'Command',
        flex: 1.3,
        editable: true,
        renderEditCell: (params) => <RequiredEditInput {...params} />,
        renderHeader: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            Command
            <Typography color="error" sx={{ ml: 0.5 }}>*</Typography>
          </Box>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1.2,
        editable: true,
        renderEditCell: (params) => <RequiredEditInput {...params} />,
        renderHeader: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            Description
            <Typography color="error" sx={{ ml: 0.5 }}>*</Typography>
          </Box>
        ),
      },
      {
        field: 'expectedResponse',
        headerName: 'Expected Response',
        flex: 1,
        editable: true,
        filterable: false,
      },
      {
        field: 'requiresInputValue',
        headerName: 'Input Required',
        width: 130,
        editable: true,
        filterable: false,
        type: 'boolean',
        renderCell: (params: GridRenderCellParams) => (
          <Checkbox checked={params.value || false} disabled />
        ),
        renderEditCell: renderCheckboxEditCell,
      },
      {
        field: 'inputValue',
        headerName: 'Input Value',
        flex: 1,
        editable: true,
        filterable: false,
        renderCell: renderInputValueCell,
        renderEditCell: renderInputValueEditCell,
      },
      {
        field: 'actions',
        type: 'actions',
        width: 100,
        headerName: 'Actions',
        getActions: ({ id }) => {
          const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;

          return isEditing
            ? [
              <GridActionsCellItem
                key="save"
                icon={<SaveIcon />}
                label="Save"
                onClick={() => handleSaveClick(String(id))}
                color="primary"
              />,
              <GridActionsCellItem
                key="cancel"
                icon={<CloseIcon />}
                label="Cancel"
                onClick={() => handleCancelClick(String(id))}
              />,
            ]
            : [
              <GridActionsCellItem
                key="edit"
                icon={<EditIcon />}
                label="Edit"
                onClick={() => handleEditClick(String(id))}
                color="primary"
              />,
              <GridActionsCellItem
                key="delete"
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => handleDeleteClick(String(id))}
                color="error"
              />,
            ];
        },
      },
    ],
    [
      rowModesModel,
      handleEditClick,
      handleSaveClick,
      handleCancelClick,
      handleDeleteClick,
      renderCheckboxEditCell,
      renderInputValueCell,
      renderInputValueEditCell,
    ]
  );

  /* ---------- Render ---------- */

  if (loading) {
    return (
      <Box
        sx={{
          height: 600,
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <CircularProgress size={48} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          height: 600,
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          gap: 2,
        }}
      >
        <Typography color="error" variant="h6">
          {error}
        </Typography>

        <Tooltip title="Retry loading commands">
          <IconButton
            onClick={loadCommands}
            color="primary"
            size="large"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: 64,
              height: 64,
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            <RefreshIcon fontSize="large" />
          </IconButton>
        </Tooltip>

        <Typography variant="body2" color="text.secondary">
          Click to retry
        </Typography>
      </Box>
    );
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      // Find indexes based on id
      const oldIndex = rows.findIndex((row) => row.id === active.id);
      const newIndex = rows.findIndex((row) => row.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Create new sorted array
        const newRows = arrayMove([...rows], oldIndex, newIndex);

        // Optimistic update
        setRows(newRows);

        // Sync to backend
        try {
          await api.put(`${API_ENDPOINT}/reorder`, {
            orderedIds: newRows.map(r => r._id || r.id)
          });
        } catch (err) {
          setToast({ message: 'Failed to save new order', severity: 'error' });
          loadCommands(); // Revert
        }
      }
    }
  };

  return (
    <Box
      sx={{
        height: 600,
        mb: 4,
        '& .MuiDataGrid-root': {
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        },
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={rows.map(r => r.id)}
          strategy={verticalListSortingStrategy}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(err) => {
              console.error('Row update error:', err);
            }}
            filterModel={filterModel}
            onFilterModelChange={(model) => {
              const text = model.quickFilterValues?.join(' ') || '';
              setSearchInput(text);
            }}
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            slots={{
              toolbar: CommandToolbar,
              row: DraggableRow,
            }}
            slotProps={{
              toolbar: {
                title: `${type.toUpperCase()} Commands`,
                rows,
                setRows,
                setRowModesModel,
                type,
                searchText: searchInput,
                setSearchText: setSearchInput,
              },
            }}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
            }}
          />

        </SortableContext>
      </DndContext>

      <Snackbar
        open={!!toast}
        autoHideDuration={TOAST_DURATION_MS}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast?.severity || 'info'}
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* -------------------- Main Page Component -------------------- */

export default function CommandRepoPage() {
  return (
    <Box sx={{ p: 4, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Command Response Repository
        </Typography>
        <Typography color="text.secondary" variant="body1">
          Manage 2G and 4G command responses for your network infrastructure.
        </Typography>
      </Box>

      <DataGridEditor type="2g" />
      <DataGridEditor type="4g" />
    </Box>
  );
}